'use client'

/* eslint-disable react/no-unescaped-entities */
import dynamic from 'next/dynamic'
import { useEffect, useMemo, type JSX } from 'react'
import { useLayout, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import {
  type ProductForListFragment,
  type ProductReferenceFragment,
  SortBy,
  Touchpoint,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'
import { type ProductCardType, useProductListSettings } from './use-product-list-settings'
import type { DigitalDataOrigin } from '../utils/digital-data-types'
import { lastIndexVar } from '@dustin-web/microsite-components/src/hooks/vars'
import { getSortBy } from './get-sort-by'
import { landingPageService, useLandingPage } from '../landing-page/data'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import TakebackWrapper from '../services-banners/takeback-wrapper'
import { TAKEBACK_BANNER_CATEGORY_IDS } from '@productsite/src/components/constants'

const LandingPageGroup = dynamic(() =>
  import('@dustin-web/product-components/src/landing-page/components').then(
    mod => mod.LandingPageGroup
  )
)

const ProductVirtualList = dynamic(() => import('./product-virtual-list'))
const HelpToFindContent = dynamic(
  () => import('@dustin-web/content-components/src/help-to-find-content')
)

const ProductCardCompact = dynamic(() => import('../cards').then(mod => mod.ProductCardCompact))
const ProductCardCompactV2 = dynamic(() => import('../cards').then(mod => mod.ProductCardCompactV2))
const ProductCardExtended = dynamic(() => import('../cards').then(mod => mod.ProductCardExtended))
const ProductCardGallerySwitch = dynamic(() =>
  import('../cards').then(mod => mod.ProductCardGallerySwitch)
)
const ProductCardStripped = dynamic(() => import('../cards').then(mod => mod.ProductCardStripped))
const ProductCardStrippedMini = dynamic(() =>
  import('../cards').then(mod => mod.ProductCardStrippedMini)
)

type ListItemProps = {
  product: ProductForListFragment
  ticket: string
  listType: ProductCardType
  imagePriority?: boolean
  inFilterList?: boolean
  isExtraItem?: boolean
  gallerySizeClasses?: string
  mainProductErpIdentifier?: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  isProductAccessories?: boolean
  showSideButtons?: boolean
  hTag?: HeadingProps['hTag']
  prevLastIndex?: number | undefined | null
}

export const ListItemRenderer = ({
  product,
  ticket,
  listType,
  imagePriority,
  inFilterList = false,
  isExtraItem = false,
  gallerySizeClasses = '',
  mainProductErpIdentifier,
  digitalDataOrigin,
  digitalIndex = 0,
  isProductAccessories = false,
  showSideButtons,
  hTag,
  prevLastIndex,
}: ListItemProps) => {
  useEffect(() => {
    if (prevLastIndex !== null && prevLastIndex === digitalIndex) {
      lastIndexVar(prevLastIndex)
    }
  }, [prevLastIndex, digitalIndex])

  if (listType === 'gallery') {
    return (
      <ProductCardGallerySwitch
        product={product}
        ticket={ticket}
        imagePriority={imagePriority}
        isExtraItem={isExtraItem}
        sizeClasses={gallerySizeClasses}
        inFilterList={inFilterList}
        mainProductErpIdentifier={mainProductErpIdentifier}
        digitalDataOrigin={digitalDataOrigin}
        digitalIndex={digitalIndex}
        showSideButtons={showSideButtons}
        isProductAccessories={isProductAccessories}
        preventSetLastUpdatedProductErpIdentifier={isProductAccessories}
        hTag={hTag}
      />
    )
  }

  if (listType === 'compact') {
    return (
      <>
        <ProductCardCompact
          product={product}
          ticket={ticket}
          imagePriority={imagePriority}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          sizeClasses={gallerySizeClasses}
          hTag={hTag}
        />
      </>
    )
  }

  if (listType === 'compact-v2') {
    return (
      <>
        <ProductCardCompactV2
          product={product}
          ticket={ticket}
          oneRowLayout
          imagePriority={imagePriority}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          sizeClasses={gallerySizeClasses}
          hTag={hTag}
        />
      </>
    )
  }

  if (listType === 'stripped') {
    return (
      <>
        <ProductCardStripped
          product={product}
          ticket={ticket}
          imagePriority={imagePriority}
          isExtraItem={isExtraItem}
          sizeClasses={gallerySizeClasses}
          inFilterList={inFilterList}
          mainProductErpIdentifier={mainProductErpIdentifier}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          isProductAccessories={isProductAccessories}
          preventSetLastUpdatedProductErpIdentifier={isProductAccessories}
          hTag={hTag}
        />
      </>
    )
  }

  if (listType === 'stripped-mini') {
    return (
      <>
        <ProductCardStrippedMini
          product={product}
          ticket={ticket}
          imagePriority={imagePriority}
          isExtraItem={isExtraItem}
          sizeClasses={gallerySizeClasses}
          inFilterList={inFilterList}
          mainProductErpIdentifier={mainProductErpIdentifier}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          isProductAccessories={isProductAccessories}
          preventSetLastUpdatedProductErpIdentifier={isProductAccessories}
          hTag={hTag}
        />
      </>
    )
  }

  if (listType === 'extended' && !isExtraItem) {
    return (
      <>
        <ProductCardExtended
          product={product}
          imagePriority={imagePriority}
          ticket={ticket}
          mainProductErpIdentifier={mainProductErpIdentifier}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          isProductAccessories={isProductAccessories}
          showSideButtons={showSideButtons}
          preventSetLastUpdatedProductErpIdentifier={isProductAccessories}
          hTag={hTag}
        />
      </>
    )
  }

  return null
}

type Props = {
  productReferences: ProductReferenceFragment[]
  digitalDataOrigin: DigitalDataOrigin
  loading?: boolean
  searchPhrase?: string
  className?: string
  infinityScroll?: boolean //set true if consuming component is using infinity scroll instead of pagination.
  virtualize?: boolean
  viewTypeOverride?: string | null
  promotedProductCard?: JSX.Element | null
  showSideButtons?: boolean
  hTag?: HeadingProps['hTag']
  categoryId?: string | undefined
}

export const ProductList = ({
  categoryId,
  productReferences,
  digitalDataOrigin,
  loading = false,
  searchPhrase,
  infinityScroll = false,
  virtualize = true,
  viewTypeOverride = null,
  promotedProductCard = null,
  showSideButtons,
  hTag,
}: Props) => {
  const { translate } = useTranslations()
  const { responsive } = useLayout()
  const { sortBy, pageSize, viewType } = useProductListSettings()

  const { enabled: isTakebackBannerEnabled } = useFeatureFlag('wt-enable-takeback-banners')
  const isSelectedCategory = TAKEBACK_BANNER_CATEGORY_IDS.has(categoryId ?? '')

  const currentViewType = viewTypeOverride || viewType

  // TODO: Remove grid and layout check and add support for virtual grid list
  const validLayoutSize = responsive.breakpoints.min.LG || responsive.breakpoints.max.SM
  const shouldVirtualize = virtualize && currentViewType !== 'gallery' && validLayoutSize

  // Price sorting from search-query (esales) is not always acurate (i.e. customer specific prices),
  // so we need to do sort with the prices from pricing-query here so at least the current page is correctly sorted.
  const validAndSortedProductReferences = useMemo(
    () => getValidAndSortedProductReferences(productReferences, sortBy),
    [productReferences, sortBy]
  )

  const { data: landingPage } = useLandingPage(
    {
      pageReference: '/no-search-results',
      touchpoint: responsive.isSmallScreen ? Touchpoint.Mobile : Touchpoint.Desktop,
      sortBy: getSortBy(sortBy),
    },
    validAndSortedProductReferences.length !== 0
  )

  const cachedLandingPageService = useMemo(
    () => landingPageService(landingPage?.landingPage ?? {}),
    [landingPage]
  )
  const landingPageGroups = cachedLandingPageService?.groups ?? new Map()
  const popularProducts = landingPageGroups.get('popular')

  const insertTakebackBannerInList = isTakebackBannerEnabled && isSelectedCategory
  const bannerInsertIndex = Math.min(5, validAndSortedProductReferences.length)
  const completeList = insertTakebackBannerInList
    ? [
        ...validAndSortedProductReferences.slice(0, bannerInsertIndex),
        { id: 'takebackBanner' } as ProductReferenceFragment,
        ...validAndSortedProductReferences.slice(bannerInsertIndex),
      ]
    : validAndSortedProductReferences

  return (
    <div className={`u-pt-8 c-product-list ${loading ? 'c-product-list--loading' : ''}`}>
      {shouldVirtualize ? (
        <ProductVirtualList
          products={completeList}
          promotedProductCard={promotedProductCard}
          showSideButtons={!!showSideButtons}
          viewType={currentViewType}
          infinityScroll={infinityScroll}
          pageSize={pageSize}
          hTag={hTag}
          digitalDataOrigin={digitalDataOrigin}
          showTakebackBanner={insertTakebackBannerInList}
          categoryId={categoryId}
        />
      ) : (
        <div className="row sm-gutters">
          {promotedProductCard && <>{promotedProductCard}</>}
          {completeList.map(productReference => {
            if (insertTakebackBannerInList && productReference.id === 'takebackBanner') {
              return (
                <TakebackWrapper
                  key={productReference.id}
                  listType={currentViewType}
                  categoryId={categoryId}
                />
              )
            }
            const productIndex = validAndSortedProductReferences.indexOf(productReference)
            return (
              productReference.searchResultProduct && (
                <ListItemRenderer
                  showSideButtons={showSideButtons}
                  key={productReference.id}
                  product={productReference.searchResultProduct}
                  ticket={productReference.ticket}
                  listType={currentViewType}
                  inFilterList
                  imagePriority={productIndex <= 6}
                  isExtraItem={infinityScroll ? false : pageSize ? productIndex >= pageSize : false}
                  digitalDataOrigin={digitalDataOrigin}
                  digitalIndex={productIndex}
                  hTag={hTag}
                  prevLastIndex={validAndSortedProductReferences.length - 1}
                />
              )
            )
          })}
        </div>
      )}
      {validAndSortedProductReferences.length === 0 && searchPhrase && (
        <>
          {searchPhrase && (
            <p className="u-overflow-wrap u-fs-24 u-mb-24">
              <strong>"{searchPhrase}" </strong>
              {translate('SearchNoHits')}
            </p>
          )}
          <p className="p-sm">{translate('SearchNoHitsDescription')}</p>
          <p className="u-mb-24 p-sm">{translate('SearchNoHitsGuidance')}</p>
          {digitalDataOrigin !== 'filterPage' &&
            digitalDataOrigin !== 'newProductsPage' &&
            digitalDataOrigin !== 'CampaignPage' && (
              <HelpToFindContent>
                {popularProducts && (
                  <LandingPageGroup removeHorizontalPadding key="popular" data={popularProducts} />
                )}
              </HelpToFindContent>
            )}
        </>
      )}
    </div>
  )
}

const getValidAndSortedProductReferences = (
  productReferences: ProductReferenceFragment[],
  sortBy: string
) => {
  const validProductReferences = productReferences.filter(pr => pr.searchResultProduct)

  if (sortBy !== SortBy.Asc.toLowerCase() && sortBy !== SortBy.Desc.toLowerCase()) {
    return validProductReferences
  }

  const sortedProductReferences = validProductReferences.sort((a, b) => {
    const aPrice = a.searchResultProduct?.price?.price || 0
    const bPrice = b.searchResultProduct?.price?.price || 0

    if (sortBy === SortBy.Asc.toLowerCase()) {
      return aPrice - bPrice
    }

    return bPrice - aPrice
  })

  return sortedProductReferences
}
